import { useCallback, useEffect, useMemo, useRef } from 'react'
import { DragFileData, DragJobData, DragTagData, FileTag } from '@/shared/utils/crud-helper/models'
import { useCognitoAuth } from '@/app/modules/auth'
import { getLogger } from '@/shared/utils/logUtils'
import { toast } from 'react-toastify'
import { toastDefaultConfig } from '@/shared/config/toastConfig'
import { useKeyedState } from './useKeyedState'
import { toCloudPath } from '../../ai-chat-v2/cloudDisplayPath'
// No bucket in rendered message content; backend can infer/attach the bucket

/** Composer key used while no conversation tab is open (empty-state composer). */
export const NO_CONVERSATION_KEY = ''

const EMPTY_TAGS: readonly FileTag[] = []

function fileTagFromDrag(dragData: DragFileData, fullKey: string, cloudPath: string): FileTag {
  const base = { id: dragData.id, name: dragData.name, fullPath: fullKey, cloudPath, source: dragData.source }
  return dragData.isDir
    ? { ...base, type: 'folder', s3Prefix: fullKey }
    : { ...base, type: 'file' }
}

function jobTagFromDrag(dragData: DragJobData): FileTag {
  return {
    id: `job:${dragData.id}`,
    name: dragData.name,
    type: 'job',
    runId: dragData.id,
    ...(dragData.tool ? { tool: dragData.tool } : {}),
  }
}

/** Add a chip, or refresh the one with the same id in place. */
function upsertTag(prev: readonly FileTag[], tag: FileTag): FileTag[] {
  const existingIndex = prev.findIndex(t => t.id === tag.id)
  if (existingIndex === -1) return [...prev, tag]
  return prev.map((t, i) => (i === existingIndex ? tag : t))
}

// Same shape the agent and the WebSocket Lambda accept for a run id.
const RUN_ID_RE = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/

/**
 * Shown when something from a folder shared with the user is dropped on the composer. The agent
 * only reads the user's own space (`cloud:` paths) until shared folders reach it (plan P2-C), so
 * such a chip would point at the wrong file or at nothing. The owner's own shared folders are
 * ordinary My Files paths and attach as usual.
 */
export const SHARED_WITH_ME_ATTACH_MESSAGE =
  "Files from folders shared with you can't be attached to the chat yet. Only files in My Files can be attached."

/**
 * File/folder chips attached in the chat composer, kept per conversation so each tab starts clean.
 *
 * `activeKey` is the conversation whose composer is shown; drops, removals and `fileTags` act on
 * it. `getFileTags` reads any conversation's chips (what a session controller sends with a turn).
 */
export const useFileTags = (activeKey: string | null = null) => {
  const tagsByKey = useKeyedState<readonly FileTag[]>()
  const tagsKey = activeKey ?? NO_CONVERSATION_KEY
  const keyRef = useRef(tagsKey)
  keyRef.current = tagsKey
  const activeDropTargetRef = useRef<HTMLElement | null>(null)
  const { currentUser } = useCognitoAuth()
  const identityId = currentUser?.identity_id || ''

  const ensureIdentityPrefix = useCallback((key: string): string => {
    if (!identityId) return key
    return key.startsWith(identityId + '/') ? key : `${identityId}/${key}`
  }, [identityId])

  const getFileTags = useCallback((conversationKey: string | null): FileTag[] => (
    [...(tagsByKey.get(conversationKey ?? NO_CONVERSATION_KEY) ?? EMPTY_TAGS)]
  ), [tagsByKey])

  const ownTags = tagsByKey.get(tagsKey) ?? EMPTY_TAGS
  const fileTags = useMemo(() => [...ownTags], [ownTags])

  // Add a chip from drag data (to the active conversation)
  const addFileTag = useCallback((dragData: DragTagData) => {
    if (dragData.type === 'job') {
      if (!RUN_ID_RE.test(dragData.id)) {
        getLogger().warn('Not attaching a job whose run id is not valid:', dragData.id)
        return
      }
      tagsByKey.update(keyRef.current, prev => upsertTag(prev ?? EMPTY_TAGS, jobTagFromDrag(dragData)))
      return
    }
    if (dragData.shareId) {
      toast.warning(SHARED_WITH_ME_ATTACH_MESSAGE, { ...toastDefaultConfig, toastId: 'chat-shared-attach' })
      return
    }
    const fullKey = dragData.s3Prefix || ensureIdentityPrefix(dragData.id)
    // The row id is already relative to the cloud root, so this does not wait on the identity.
    const cloudPath = toCloudPath(dragData.id, { isFolder: dragData.isDir, identityId })
    if (!cloudPath) {
      getLogger().warn('Not attaching a file whose path is not a valid cloud: path:', dragData.id)
      return
    }
    tagsByKey.update(keyRef.current, prev => upsertTag(prev ?? EMPTY_TAGS, fileTagFromDrag(dragData, fullKey, cloudPath)))
  }, [ensureIdentityPrefix, identityId, tagsByKey])

  // Remove a file tag by ID (from the active conversation)
  const removeFileTag = useCallback((tagId: string) => {
    tagsByKey.update(keyRef.current, prev => {
      const next = (prev ?? EMPTY_TAGS).filter(tag => tag.id !== tagId)
      return next.length > 0 ? next : undefined
    })
  }, [tagsByKey])

  // Clear a conversation's file tags (the active one when no key is given)
  const clearFileTags = useCallback((conversationKey?: string | null) => {
    tagsByKey.update(conversationKey ?? keyRef.current, () => undefined)
  }, [tagsByKey])

  /** Give chips attached in the empty-state composer to the conversation just created for them. */
  const adoptNoConversationTags = useCallback((conversationKey: string) => {
    tagsByKey.move(NO_CONVERSATION_KEY, conversationKey)
  }, [tagsByKey])

  /** Forget chips of conversations whose tab is closed. */
  const retainFileTags = useCallback((openKeys: readonly string[]) => {
    tagsByKey.retain([NO_CONVERSATION_KEY, ...openKeys])
  }, [tagsByKey])

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
    const target = e.currentTarget as HTMLElement
    activeDropTargetRef.current = target
    target.classList.add('drop-active')
  }, [])

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    const target = e.currentTarget as HTMLElement
    activeDropTargetRef.current = target
    target.classList.add('drop-active')
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    // Intentionally no-op: keep highlight until drop or dragend
    e.preventDefault()
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    const target = e.currentTarget as HTMLElement
    target.classList.remove('drop-active')
    if (activeDropTargetRef.current === target) {
      activeDropTargetRef.current = null
    }

    try {
      const dragDataRaw = e.dataTransfer.getData('application/json')
      if (!dragDataRaw) return
      const parsed = JSON.parse(dragDataRaw) as DragTagData | DragTagData[]
      const items: DragTagData[] = Array.isArray(parsed) ? parsed : [parsed]
      items.forEach((dragData) => addFileTag(dragData))
    } catch (error) {
      console.error('Failed to parse drag data:', error)
    }
  }, [addFileTag])

  // Ensure highlight is cleared if drag operation ends outside the target
  useEffect(() => {
    const clearActiveHighlight = () => {
      if (activeDropTargetRef.current) {
        activeDropTargetRef.current.classList.remove('drop-active')
        activeDropTargetRef.current = null
      }
    }

    window.addEventListener('dragend', clearActiveHighlight)
    window.addEventListener('drop', clearActiveHighlight)

    return () => {
      window.removeEventListener('dragend', clearActiveHighlight)
      window.removeEventListener('drop', clearActiveHighlight)
    }
  }, [])

  // Process file tags and convert to S3 paths for message content
  const processFileTagsInContent = useCallback((content: string): string => {
    let processedContent = content

    // Process file tags and convert to S3 paths
    fileTags.forEach(tag => {
      if (tag.type === 'job') return
      if (tag.type === 'folder') {
        // For folders: send S3 prefix for listing contents
        processedContent = processedContent.replace(
          new RegExp(`\\[${tag.name}\\]`, 'g'), 
          `s3://${tag.s3Prefix}` // Prefix without bucket
        )
      } else {
        // For files: send full S3 path
        processedContent = processedContent.replace(
          new RegExp(`\\[${tag.name}\\]`, 'g'), 
          `s3://${tag.fullPath}`
        )
      }
    })

    return processedContent
  }, [fileTags])

  // Create default message content when only file tags are present
  const createFileOnlyMessage = useCallback((inputValue: string): string => {
    if (!inputValue.trim() && fileTags.length > 0) {
      return `Please analyze the following ${fileTags.length === 1 ? 'file' : 'files'}: ${fileTags.map(tag => `[${tag.name}]`).join(', ')}`
    }
    return inputValue.trim()
  }, [fileTags])

  // Check if send button should be disabled
  const isSendDisabled = useCallback((inputValue: string, isLoading: boolean): boolean => {
    return (!inputValue.trim() && fileTags.length === 0) || isLoading
  }, [fileTags.length])

  return {
    // State
    fileTags,
    
    // File tag management
    getFileTags,
    addFileTag,
    removeFileTag,
    clearFileTags,
    adoptNoConversationTags,
    retainFileTags,
    
    // Drag and drop handlers
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    
    // Utility functions
    processFileTagsInContent,
    createFileOnlyMessage,
    isSendDisabled,
  }
} 